import type { TFunction } from "i18next";
import {
  ProcurementOverviewFigure,
  type ProcurementFigureFilterDto,
  type ProcurementOverviewScopeDto,
} from "../../autogen/bff-api";
import { urls } from "../../urls";

export type OverviewPeriod = "month" | "quarter" | "year" | "lastYear" | "custom";

export const PERIODS: OverviewPeriod[] = ["month", "quarter", "year", "lastYear", "custom"];

export const DEFAULT_PERIOD: OverviewPeriod = "quarter";

export const parsePeriod = (value: string | null): OverviewPeriod =>
  PERIODS.find((period) => period === value) ?? DEFAULT_PERIOD;

export type PeriodRange = { from: string; to: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const periodRange = (
  period: OverviewPeriod,
  today: Date,
  custom: { from: string | null; to: string | null },
): PeriodRange | null => {
  const year = today.getFullYear();
  const month = today.getMonth();
  switch (period) {
    case "month":
      return span(year, month, year, month);
    case "quarter": {
      const first = month - (month % 3);
      return span(year, first, year, first + 2);
    }
    case "year":
      return span(year, 0, year, 11);
    case "lastYear":
      return span(year - 1, 0, year - 1, 11);
    case "custom": {
      const { from, to } = custom;
      return from && to && ISO_DATE.test(from) && ISO_DATE.test(to) && from <= to ? { from, to } : null;
    }
  }
};

const span = (fromYear: number, fromMonth: number, toYear: number, toMonth: number): PeriodRange => ({
  from: isoDate(new Date(fromYear, fromMonth, 1)),
  to: isoDate(new Date(toYear, toMonth + 1, 0)),
});

const isoDate = (date: Date): string =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, "0")).join("-");

export const periodLabel = (t: TFunction, period: OverviewPeriod): string =>
  ({
    month: t("This month"),
    quarter: t("This quarter"),
    year: t("This year"),
    lastYear: t("Last year"),
    custom: t("Custom"),
  })[period];

export const LIST_FIGURE_PARAM = "figure";

export type ListFigure = ProcurementOverviewFigure | "byProcedure" | "byRegulation";

export const procurementListLink = (filter: ProcurementFigureFilterDto, figure: ListFigure): string => {
  const params = new URLSearchParams();
  Object.entries(filter).forEach(([key, value]) => {
    if (value == null) return;
    if (key === "deadlinePassed") {
      if (value) params.set("deadline", "passed");
      return;
    }
    params.set(key, Array.isArray(value) ? value.join(",") : String(value));
  });
  params.set(LIST_FIGURE_PARAM, figure);
  return `${urls.app.sourcingEvents.viewAll}?${params}`;
};

export const FIGURE_ORDER: ProcurementOverviewFigure[] = [
  ProcurementOverviewFigure.Published,
  ProcurementOverviewFigure.Awarded,
  ProcurementOverviewFigure.MedianCycleDays,
  ProcurementOverviewFigure.AverageBids,
  ProcurementOverviewFigure.OneBidShare,
  ProcurementOverviewFigure.NoBids,
];

export const procurementFigureLabel = (t: TFunction, figure: ProcurementOverviewFigure): string =>
  ({
    [ProcurementOverviewFigure.Published]: t("Published in the period"),
    [ProcurementOverviewFigure.Awarded]: t("Awarded in the period"),
    [ProcurementOverviewFigure.MedianCycleDays]: t("Median cycle time"),
    [ProcurementOverviewFigure.AverageBids]: t("Average bids per competition"),
    [ProcurementOverviewFigure.OneBidShare]: t("Competitions with one bid"),
    [ProcurementOverviewFigure.NoBids]: t("No bids"),
  })[figure];

export const procurementFigureDefinition = (t: TFunction, figure: ProcurementOverviewFigure): string =>
  ({
    [ProcurementOverviewFigure.Published]: t(
      "Procurements that were published in the period, whatever their status is now. A procurement published again after being reopened counts once."
    ),
    [ProcurementOverviewFigure.Awarded]: t(
      "Procurements that were awarded in the period, whatever their procedure and whatever their status is now. An award that waits for its standstill period counts once it is confirmed."
    ),
    [ProcurementOverviewFigure.MedianCycleDays]: t(
      "The median number of days from a procurement was first published until it was awarded, for the procurements awarded in the period."
    ),
    [ProcurementOverviewFigure.AverageBids]: t(
      "Delivered bids per competition whose bid deadline passed in the period. Competitions with no bids are counted on their own, not in the average. Procurements without competition, dynamic purchasing systems and qualification schemes are not competitions here."
    ),
    [ProcurementOverviewFigure.OneBidShare]: t(
      "The share of the competitions with delivered bids, whose bid deadline passed in the period, that got exactly one bid."
    ),
    [ProcurementOverviewFigure.NoBids]: t(
      "Competitions whose bid deadline passed in the period without a single delivered bid."
    ),
  })[figure];

export const procurementScopeText = (t: TFunction, scope: ProcurementOverviewScopeDto): string => {
  if (scope.onlyOwnProcurements) return String(t("the procurements you created or are responsible for"));
  if (scope.organizationWide) return String(t("the whole organisation"));
  return [
    ...scope.departmentNames,
    ...(scope.projects > 0 ? [String(t("procurements in {{count}} projects", { count: scope.projects }))] : []),
    ...(scope.sharedProcurements > 0
      ? [String(t("{{count}} procurements shared with you", { count: scope.sharedProcurements }))]
      : []),
  ].join(", ");
};
